import type { AgentThreadMessage } from "@/store/chat.types";
import { assembleMessageList } from "@/store/helpers/assembleMessageList";
import { mergeStreamingMessages } from "@/store/helpers/mergeStreamingMessages";
import {
  createOptimisticMessage,
  omitOptimisticMessages,
} from "@/store/helpers/optimisticMessageHelper";
import { action, computed, makeAutoObservable, observable } from "mobx";
import { AgentThreadVoStatus } from "../api";

type SetKeys = keyof Pick<ChatStore, "rawMessages">;

export class ChatStore {
  @observable rawMessages: AgentThreadMessage[] = [];
  @observable threadId: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  @computed
  get latestMsgHash() {
    if (!this.messages || this.messages.length === 0) {
      return "";
    }

    const latestMessage = this.messages[this.messages.length - 1];

    return JSON.stringify(latestMessage);
  }

  @action setThreadId(id: string) {
    if (this.threadId && this.threadId !== id) {
      this.set("rawMessages", []);
    }
    this.threadId = id;
  }

  @action addOptimisticUserMessage(
    text: string,
    files: File[],
    urls: string[] = [],
  ) {
    if (!this.threadId) return;
    const message = createOptimisticMessage(text, files, urls);
    this.rawMessages.push(message);
  }

  @action removeOptimisticUserMessage() {
    this.rawMessages = omitOptimisticMessages(this.rawMessages);
  }

  @action setRawMessages(threadId: string, messages: AgentThreadMessage[]) {
    if (this.threadId && this.threadId !== threadId) {
      return;
    }
    this.rawMessages = omitOptimisticMessages(messages);
  }

  @action setStreamingMessages(
    threadId: string,
    threadStatus: AgentThreadVoStatus,
    messages: AgentThreadMessage[],
  ) {
    if (this.threadId && this.threadId !== threadId) {
      return;
    }

    let filteredMessages = this.rawMessages;

    const onlyCheckpoints = messages.every(
      (m) => (m.type as string) === "checkpoint",
    );

    if (
      messages.length > 0 &&
      threadStatus !== AgentThreadVoStatus.Pending &&
      !onlyCheckpoints
    ) {
      filteredMessages = omitOptimisticMessages(this.rawMessages);
    }

    const mergedStreaming = mergeStreamingMessages(filteredMessages, messages);
    this.rawMessages = mergedStreaming;
  }

  @computed
  get messages() {
    return assembleMessageList(this.rawMessages);
  }

  @action set<K extends SetKeys>(k: K, v: ChatStore[K]) {
    this[k] = v as never;
  }

  @action clearThread() {
    this.threadId = null;
    this.set("rawMessages", []);
  }

  @action clearMessagesAfterCheckpoint(commitHash: string) {
    if (!commitHash) return;

    // Find the checkpoint message index
    const checkpointIdx = this.rawMessages.findIndex(
      (m) => (m.type as string) === "checkpoint" && m.commitHash === commitHash,
    );

    if (checkpointIdx === -1) return;

    // Find the latest restore_checkpoint message index with matching commitHash
    let restoreIdx = -1;
    for (let i = this.rawMessages.length - 1; i > checkpointIdx; i--) {
      const m = this.rawMessages[i];
      if (
        m.content?.func_name === "restore_checkpoint" &&
        m.content?.args?.commit_hash === commitHash
      ) {
        restoreIdx = i;
        break;
      }
    }

    if (restoreIdx !== -1) {
      // Remove messages from index checkpointIdx + 1 up to restoreIdx (inclusive)
      this.rawMessages.splice(checkpointIdx + 1, restoreIdx - checkpointIdx);
    }
  }

  @action updateMessageVoting(messageId: string, voting: number) {
    const message = this.rawMessages.find((m) => m.id === messageId);
    if (message) {
      message.voting = voting;
    }
  }
}

export const chatStore = new ChatStore();

export { isAssistantIteration } from "@/store/chat.types";
export type {
  AgentThreadAssistantIteration,
  AgentThreadMessage,
  AgentThreadMessageContent,
  AgentThreadMessageItem,
} from "@/store/chat.types";
export { assembleMessageList };
